<script setup>
// Trening: rodzaj z listy, moment, czas, aktywne kalorie i opcjonalny dystans.
// API nie przyjmuje tętna ani tempa — tempo policzy się z dystansu i czasu.
// Nie ma też statusu: każdy trening w API to trening wykonany.
import { computed } from 'vue';
import WhenFields from '@/components/v2/add/when-fields.vue';
import SelectField from '@/components/v2/select-field.vue';
import { useV2Day } from '@/composables/useV2Day.js';
import { intlLocale } from '@/composables/useLanguage.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    draft: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    workoutTypes: { type: Array, default: () => [] },
    // Ostatnie treningi — do powtórzenia bez wpisywania wszystkiego od nowa.
    recentWorkouts: { type: Array, default: () => [] },
    isEdit: { type: Boolean, default: false },
});
const emit = defineEmits(['pick-workout']);
const { t } = useI18n();

const { durMin, distance } = useV2Day();

const recentOptions = computed(() => props.recentWorkouts.map(w => ({
    uuid: w.uuid,
    label: [
        w.type?.name ?? t('entry.workout.fallbackName'),
        distance(w.distance_meters),
        durMin(w.duration_seconds),
        new Date(w.performed_at).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short' }),
    ].filter(Boolean).join(' · '),
})));

const selectedSource = computed({
    get: () => props.draft.sourceWorkout,
    set: (uuid) => emit('pick-workout', uuid),
});
</script>

<template>
    <div>
        <!-- Powtórzenie wcześniejszego treningu zamiast wpisywania od nowa -->
        <div v-if="!isEdit && recentOptions.length" class="mb-4">
            <label class="v2-label" for="v2-workout-pick">{{ t('entry.workout.repeat') }}</label>
            <SelectField
                id="v2-workout-pick"
                v-model="selectedSource"
                :options="recentOptions.map(o => ({ value: o.uuid, label: o.label }))"
                :empty-label="t('entry.workout.fromScratch')"
                :search-placeholder="t('entry.workout.searchWorkout')"
            />
        </div>

        <div class="mb-4">
            <label class="v2-label" for="v2-wtype">{{ t('entry.workout.type') }}</label>
            <SelectField
                id="v2-wtype"
                v-model="draft.workoutType"
                :options="workoutTypes.map(wt => ({ value: wt.uuid, label: wt.name }))"
                :placeholder="t('entry.workout.pickType')"
                :search-placeholder="t('entry.workout.searchType')"
                :invalid="!!errors.workoutType"
            />
            <p v-if="errors.workoutType" class="text-[11.5px] text-[#B5643C] mt-1">{{ errors.workoutType }}</p>
            <p v-else-if="!workoutTypes.length" class="text-[11.5px] text-[#8A8378] mt-1">
                {{ t('entry.workout.noTypes') }}
            </p>
        </div>

        <div class="mb-4">
            <WhenFields :draft="draft" :errors="errors" :time-label="t('entry.workout.start')" />
        </div>

        <div class="mb-4">
            <span class="v2-label">{{ t('entry.workout.status') }}</span>
            <div class="inline-flex rounded-lg border border-[#E4DDD1] p-0.5 bg-[#FAF7F2]">
                <button
                    v-for="s in [{ v: 'done', l: t('entry.workout.done') }, { v: 'planned', l: t('entry.workout.planned') }]"
                    :key="s.v"
                    type="button"
                    class="px-3 py-1.5 rounded-[7px] text-[12.5px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C0764B]"
                    :class="draft.workoutStatus === s.v ? 'bg-[#211E1A] text-[#FBF9F4] font-semibold' : 'text-[#6B655C] hover:text-[#211E1A]'"
                    @click="draft.workoutStatus = s.v"
                >{{ s.l }}</button>
            </div>
            <p v-if="draft.workoutStatus === 'planned'" class="text-[11px] text-[#B8B0A3] mt-1.5">
                {{ t('entry.workout.plannedHint') }}
            </p>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
                <label class="v2-label" for="v2-dur-h">{{ t('entry.workout.duration') }} <span class="text-[#B8B0A3]">{{ t('entry.workout.hoursUnit') }}</span></label>
                <input id="v2-dur-h" v-model="draft.durationH" type="number" min="0" inputmode="numeric" class="v2-input v2-input--num" placeholder="0" :aria-invalid="!!errors.duration" />
            </div>
            <div>
                <label class="v2-label" for="v2-dur-m">{{ t('entry.workout.duration') }} <span class="text-[#B8B0A3]">{{ t('entry.workout.minutesUnit') }}</span></label>
                <input id="v2-dur-m" v-model="draft.durationMin" type="number" min="0" max="59" inputmode="numeric" class="v2-input v2-input--num" placeholder="30" :aria-invalid="!!errors.duration" />
            </div>
            <div>
                <label class="v2-label" for="v2-kcal">{{ t('entry.workout.calories') }} <span class="text-[#B8B0A3]">kcal</span></label>
                <input id="v2-kcal" v-model="draft.activeCalories" type="number" min="0" inputmode="numeric" class="v2-input v2-input--num" placeholder="300" :aria-invalid="!!errors.activeCalories" />
            </div>
            <div>
                <label class="v2-label" for="v2-dist">{{ t('entry.workout.distance') }} <span class="text-[#B8B0A3]">km</span></label>
                <input id="v2-dist" v-model="draft.distanceKm" type="number" min="0" step="0.01" inputmode="decimal" class="v2-input v2-input--num" placeholder="—" />
            </div>
        </div>
        <p v-if="errors.duration" class="text-[11.5px] text-[#B5643C] mt-1.5">{{ errors.duration }}</p>
        <p v-if="errors.activeCalories" class="text-[11.5px] text-[#B5643C] mt-1.5">{{ errors.activeCalories }}</p>
    </div>
</template>
